import { useMemo } from 'react';
import { AvailableTools, useEditorProps } from '@bay-email/editor';

const WEB_SAFE_FONT_FAMILIES = new Set([
	'arial',
	'helvetica',
	'helvetica neue',
	'times new roman',
	'times',
	'georgia',
	'courier new',
	'courier',
	'verdana',
	'tahoma',
	'trebuchet ms',
	'lucida sans unicode',
	'lucida grande',
	'sans-serif',
	'serif',
	'monospace',
	'cursive',
	'fantasy',
	'system-ui',
	'-apple-system',
	'blinkmacsystemfont',
	'segoe ui',
	'roboto',
]);

function extractPrimaryFontFamily(fontFamily: string) {
	return fontFamily
		.split(',')[0]
		?.trim()
		.replace(/^['"]|['"]$/g, '');
}

export const DEFAULT_RTE_TOOLS: AvailableTools[] = [
	AvailableTools.FontFamily,
	AvailableTools.FontSize,
	AvailableTools.TextTransform,
	AvailableTools.TextWrap,
	AvailableTools.Bold,
	AvailableTools.Italic,
	AvailableTools.StrikeThrough,
	AvailableTools.Underline,
	AvailableTools.IconFontColor,
	AvailableTools.IconBgColor,
	AvailableTools.TableCellBgColor,
	AvailableTools.Link,
	AvailableTools.Justify,
	AvailableTools.Lists,
	AvailableTools.HorizontalRule,
	// 13244490397: Dynamic Fields sits before Erase per the text-styling reqs.
	AvailableTools.MergeTags,
	AvailableTools.RemoveFormat,
];

// Brand colour swatches: shown in the colour pickers, applied as dynamic merge
// tags (e.g. {{user.brandPrimaryColor}}) so the email re-colours if the brand
// palette changes. The hex is only for display in the picker.
export function useBrandColors() {
	const { mergeTags, mergeTagGenerate } = useEditorProps();
	return useMemo(() => {
		const user = (mergeTags?.user || {}) as Record<string, string>;
		const gen = (path: string) =>
			mergeTagGenerate ? mergeTagGenerate(path) : `{{${path}}}`;
		const out: Array<{ label: string; color: string; value: string }> = [];
		if (user.brandPrimaryColor) {
			out.push({
				label: 'Primary',
				color: user.brandPrimaryColor,
				value: gen('user.brandPrimaryColor'),
			});
		}
		if (user.brandSecondaryColor) {
			out.push({
				label: 'Secondary',
				color: user.brandSecondaryColor,
				value: gen('user.brandSecondaryColor'),
			});
		}
		return out;
	}, [mergeTags, mergeTagGenerate]);
}

export function ensurePreviewFontLoaded(fontFamily: string) {
	const primaryFontFamily = extractPrimaryFontFamily(fontFamily);
	if (!primaryFontFamily) return;

	if (WEB_SAFE_FONT_FAMILIES.has(primaryFontFamily.toLowerCase())) {
		return;
	}

	const href = `https://fonts.googleapis.com/css2?family=${primaryFontFamily.replace(/\s+/g, '+')}&display=swap`;
	const existingLink = document.head.querySelector(
		`link[data-bay-email-preview-font="true"][href="${CSS.escape(href)}"]`,
	);
	if (existingLink) return;

	const link = document.createElement('link');
	link.rel = 'stylesheet';
	link.href = href;
	link.setAttribute('data-bay-email-preview-font', 'true');
	document.head.appendChild(link);

	if ('fonts' in document) {
		void (document as Document & { fonts?: FontFaceSet }).fonts?.load?.(
			`16px "${primaryFontFamily}"`,
		);
	}
}
